// src/pages/users/components/UserFormPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";

/** Page chrome shared by the create and edit user screens. */
export default function UserFormPage({
  title,
  subtitle,
  rightSlot,
  isLoading = false,
  loadingLabel = "Loading…",
  error = null,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Secondary action for the header, e.g. Reset Password. */
  rightSlot?: React.ReactNode;
  isLoading?: boolean;
  loadingLabel?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  const backTo = "/hr/users";

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to={backTo}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            <FiArrowLeft />
            Back to Users
          </Link>

          <h1 className="mt-2 text-xl font-semibold text-gray-900">{title}</h1>
          {subtitle && (
            <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
          )}

          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        {rightSlot}
      </div>

      <div className="max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
            {loadingLabel}
          </div>
        )}

        {!isLoading && error && (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              {error}
            </div>
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiArrowLeft />
              Back to Users
            </Link>
          </div>
        )}

        {!isLoading && !error && children}
      </div>
    </AdminLayout>
  );
}
